import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, LogOut } from "lucide-react";
import { StatusPill } from "@/components/network/StatusPill";
import { SELECT_CLS, apiError } from "@/lib/network";

const ctName = (label, kode) => ((kode || "").startsWith(label) ? kode : `${label} ${kode}`);

function AddSplitterDialog({ open, odp, onClose, onDone }) {
  const [devs, setDevs] = useState(null);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setDevs(null); setPick(""); setQ("");
    api.get("/network/server-devices/splitter").then((r) => setDevs(r.data)).catch(() => setDevs([]));
  }, [open]);
  const list = useMemo(() => (devs || []).filter((d) => !q || [d.kode, d.nama].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()))), [devs, q]);
  useEffect(() => { if (pick && !list.some((d) => d.id === pick)) setPick(""); }, [list, pick]);
  const dev = (devs || []).find((d) => d.id === pick);
  const other = dev?.server_id && dev.server_id !== odp.id;
  const submit = async () => {
    setBusy(true);
    try { await api.post("/network/server-members", { kind: "splitter", device_id: dev.id, server_id: odp.id }); toast.success(`${dev.kode} dipasang di dalam ${odp.kode}`); onDone(); }
    catch (err) { toast.error(apiError(err), { duration: 8000 }); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="odp-add-splitter-dialog">
        <DialogHeader><DialogTitle>Pasang Splitter di dalam {odp.kode}</DialogTitle></DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kode / nama splitter…" className="h-9" data-testid="odp-add-splitter-search" />
        <select className={`${SELECT_CLS} w-full`} value={pick} onChange={(e) => setPick(e.target.value)} disabled={!devs} data-testid="odp-add-splitter-device">
          <option value="">{devs ? `— Pilih (${list.length}) —` : "Memuat…"}</option>
          {list.map((d) => <option key={d.id} value={d.id} disabled={d.server_id === odp.id}>
            {d.kode} · {d.nama}{d.server_kode ? ` (di dalam ${ctName(d.container_label, d.server_kode)})` : ""}</option>)}</select>
        {other && <p className="rounded-md border border-[#FCD34D] bg-[#FEF3C7] p-2 text-xs text-[#92400E]" data-testid="odp-add-splitter-other">
          {dev.kode} saat ini di dalam {ctName(dev.container_label, dev.server_kode)}. Keluarkan dari {dev.container_label} terlebih dahulu.</p>}
        <p className="text-xs text-[#6B7280]">Hanya penempatan fisik: lokasi splitter mengikuti ODP ini. Relasi input/output optik tidak dibuat atau diubah.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Batal</Button>
          <Button disabled={!dev || busy || other} onClick={submit} data-testid="odp-add-splitter-submit">Pasang</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Splitter yang dipasang di dalam kotak ODP (keanggotaan kontainer, terpisah dari relasi optik).
export function OdpSplitters({ odpId, me, onSelect, onChanged }) {
  const [d, setD] = useState(null);
  const [k, setK] = useState(0);
  const [add, setAdd] = useState(false);
  const [rm, setRm] = useState(null);
  useEffect(() => { api.get(`/network/odp-members/${odpId}`).then((r) => setD(r.data)).catch(() => setD(false)); }, [odpId, k]);
  if (!d) return null;
  const canManage = !!me?.can_manage && !d.archived;
  const done = () => { setAdd(false); setRm(null); setK((x) => x + 1); onChanged?.(); };
  const remove = async (m) => {
    try { await api.delete(`/network/server-members/splitter/${m.id}`); toast.success(`${m.kode} dikeluarkan dari ${d.kode}. Lokasi peta splitter belum ditentukan.`); done(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <section className="rounded-md border border-[#E5E7EB] p-2 text-xs" data-testid="odp-splitters">
      <div className="flex items-center justify-between"><b className="uppercase text-[#6B7280]">Splitter di dalam ODP ({d.members.length})</b>
        {canManage && <Button size="sm" variant="outline" className="h-7" onClick={() => setAdd(true)} data-testid="odp-add-splitter-btn"><Plus className="mr-1 h-3.5 w-3.5" />Pasang</Button>}</div>
      <ul className="mt-1 space-y-1">{d.members.map((m) => (
        <li key={m.id} className="flex items-center justify-between gap-2 rounded bg-[#F8F9FA] p-1.5" data-testid={`odp-splitter-${m.kode}`}>
          <span className="min-w-0">{onSelect ? <button className="font-mono-tab font-semibold text-[#0055FF] hover:underline" onClick={() => onSelect({ kind: "splitter", id: m.id })} data-testid={`odp-splitter-open-${m.kode}`}>{m.kode}</button>
            : <b className="font-mono-tab">{m.kode}</b>}<span className="text-[#6B7280]"> · {m.nama}</span> <StatusPill status={m.status} /></span>
          {canManage && <Button size="icon" variant="ghost" className="h-7 w-7" title="Keluarkan" onClick={() => setRm(m)} data-testid={`odp-splitter-remove-${m.kode}`}><LogOut className="h-3.5 w-3.5 text-[#B91C1C]" /></Button>}
        </li>))}</ul>
      {!d.members.length && <p className="mt-1 text-[#6B7280]" data-testid="odp-splitters-empty">Belum ada splitter terpasang di dalam ODP ini.</p>}
      {d.members.length > 0 && <p className="mt-1 text-[11px] text-[#6B7280]">Lokasi splitter mengikuti ODP ini; relasi input/output optik dikelola terpisah.</p>}
      {canManage && <AddSplitterDialog open={add} odp={d} onClose={() => setAdd(false)} onDone={done} />}
      <AlertDialog open={!!rm} onOpenChange={(v) => !v && setRm(null)}>
        <AlertDialogContent data-testid="odp-remove-splitter-dialog">
          <AlertDialogHeader><AlertDialogTitle>Keluarkan {rm?.kode} dari {d.kode}?</AlertDialogTitle>
            <AlertDialogDescription>Hanya penempatan fisik yang dihapus. Relasi input/output optik tidak berubah; lokasi peta splitter perlu ditetapkan ulang.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="odp-remove-splitter-cancel">Batal</AlertDialogCancel>
            <Button onClick={() => remove(rm)} data-testid="odp-remove-splitter-confirm">Keluarkan</Button></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
